import {
  DomainPreferences,
  NonCoreDashboardPageToChartSettingKeys,
  SQLFilters,
} from '../../../api/types';
import { Languages } from '../../../constants';
import { StoryboardDateUtils } from '../../../settings/dashboard-configs/utils';
import {
  MetricIdTypeArrays,
  OverTimeTimeSelection,
  RegularMetricIdType,
  Segmentation,
  SingleValueTimeSelection,
} from '../../../types';
import { CustomChartWrapper } from '../charts/custom-chart-components/custom-chart-types';
import { TimeSliderTypes } from '../timeslider/types';

export type ChartTypeConfig =
  | OverTimeTableConfig
  | TableViewTimePeriodConfig
  | AreaChartConfig
  | LineChartConfig
  | BarChartConfig
  | PieChartConfig
  | KPIV2Config;

export interface OverTimeTableConfig {
  chartType: 'table';
  timeframe: 'overTime';
  title?: string;
}

export interface TableViewTimePeriodConfig {
  chartType: 'table';
  timeframe: 'timePeriod';
  title?: string;
}

export interface OverTimeBarChartConfig {
  chartType: 'bar';
  timeframe: 'overTime';
  title?: string;
  stack?: boolean;
  relative?: boolean;
}

export interface TimePeriodBarChartConfig {
  chartType: 'bar';
  timeframe: 'timePeriod';
  title?: string;
  stack?: boolean;
  relative?: boolean;
}

export type BarChartConfig = TimePeriodBarChartConfig | OverTimeBarChartConfig;

export interface LineChartConfig {
  chartType: 'line';
  title?: string;
}

export interface PieChartConfig {
  chartType: 'pie';
  variant?: 'donut' | 'pie';
}

export interface KPIV2Config {
  chartType: 'kpi-v2';
}
export interface AreaChartConfig {
  chartType: 'area';
  title?: string;
  stack?: boolean;
}

export interface CustomChartConfig {
  chartType: 'custom';
}

type GridColSpan = number;

type GridRowSpan = number;

type GridCols = number;

type GridRows = number;

type GridColStart = number;

type GridColEnd = number;

type GridRowStart = number;

type GridRowEnd = number;

export type LanguageString = Record<Languages, string>;

export interface OverTimeTableQueryConfig {
  metrics: RegularMetricIdType[];
  timeSelection: OverTimeTimeSelection;
  filters?: SQLFilters;
  segmentations?: Segmentation[];
}

export interface LineChartQueryConfig {
  isBenchmark?: boolean;
  xAxisId?: number;
  metrics: MetricIdTypeArrays;
  timeSelection: OverTimeTimeSelection;
  filters?: SQLFilters;
  segmentations?: Segmentation[];
}

export interface ChartGridConfig {
  gridColSpan?: GridColSpan;
  gridRowSpan?: GridRowSpan;
  gridColStart?: GridColStart;
  gridColEnd?: GridColEnd;
  gridRowStart?: GridRowStart;
  gridRowEnd?: GridRowEnd;
}

export interface BaseConfig {
  title?: LanguageString;
  subtitle?: LanguageString;
  footer?: LanguageString;
  chartId: string;
  classNames?: string;
}

export interface OvertimeTableComponentConfig
  extends ChartGridConfig,
    BaseConfig {
  chartTypeConfig: OverTimeTableConfig;
  queries: OverTimeTableQueryConfig[];
}

export interface TableViewTimePeriodQueryConfig {
  metric: RegularMetricIdType;
  timeSelection: SingleValueTimeSelection;
  filters?: SQLFilters;
  segmentations: Segmentation[];
}

export interface TableViewTimePeriodComponentConfig
  extends ChartGridConfig,
    BaseConfig {
  chartTypeConfig: TableViewTimePeriodConfig;
  query: TableViewTimePeriodQueryConfig;
}

export interface LineChartComponentConfig extends ChartGridConfig, BaseConfig {
  chartTypeConfig: LineChartConfig | AreaChartConfig;
  colors?: string[];
  queries: LineChartQueryConfig[];
}

export interface CustomChartComponentConfig
  extends ChartGridConfig,
    BaseConfig {
  chartTypeConfig: CustomChartConfig;
  customComponent: CustomChartWrapper;
}

export interface TimePeriodBarChartQueryConfig {
  metrics: MetricIdTypeArrays;
  timeSelection: SingleValueTimeSelection;
  filters?: SQLFilters;
  segmentations?: Segmentation[];
}

export interface TimePeriodBarChartComponentConfig
  extends ChartGridConfig,
    BaseConfig {
  chartTypeConfig: TimePeriodBarChartConfig;
  colors?: string[];
  queries: TimePeriodBarChartQueryConfig[];
}

export interface OverTimeBarChartQueryConfig {
  isBenchmark?: boolean;
  xAxisId?: number;
  metrics: MetricIdTypeArrays;
  timeSelection: OverTimeTimeSelection;
  filters?: SQLFilters;
  segmentations?: Segmentation[];
}

export interface OverTimeBarChartComponentConfig
  extends ChartGridConfig,
    BaseConfig {
  chartTypeConfig: OverTimeBarChartConfig;
  colors?: string[];
  queries: OverTimeBarChartQueryConfig[];
}

export type BarChartComponentConfig =
  | TimePeriodBarChartComponentConfig
  | OverTimeBarChartComponentConfig;

interface PieChartQueryConfig {
  metrics: RegularMetricIdType[];
  timeSelection: SingleValueTimeSelection;
  filters?: SQLFilters;
  segmentations?: Segmentation[];
}

export interface PieChartComponentConfig extends ChartGridConfig, BaseConfig {
  chartTypeConfig: PieChartConfig;
  colors?: string[];
  query: PieChartQueryConfig;
}

export interface KpiQueryConfig {
  metrics: MetricIdTypeArrays;
  timeSelection: SingleValueTimeSelection;
  filters?: SQLFilters;
  segmentations?: Segmentation[];
}

export enum KpiFactboxBenchmarkTypes {
  VALUE = 'value',
  DELTA = 'delta',
  DELTA_PERCENT = 'deltaPercent',
}

export enum KpiFactboxBenchmarkPeriods {
  PRIOR_PERIOD = 'priorPeriod',
  YEAR_ON_YEAR = 'yearOnYear',
}

export interface KpiBenchmarkConfig {
  benchmarkTitle?: LanguageString;
  benchmarkPeriod: KpiFactboxBenchmarkPeriods;
  benchmarkType: KpiFactboxBenchmarkTypes;
}

export interface KpiSegmentationConfig {
  // default is true
  showMainValue?: boolean;
}

export interface KpiV2SubQueryConfig {
  title?: LanguageString;
  query: Pick<KpiQueryConfig, 'metrics' | 'timeSelection' | 'filters'>;
}

export interface KpiComponentSectionConfig {
  key: string;
  title?: LanguageString;
  subtitle?: LanguageString;
  colors?: string[];
  query: KpiQueryConfig;
  benchmarkConfigs?: KpiBenchmarkConfig[];
  segmentationConfig?: KpiSegmentationConfig;
  subQueries?: KpiV2SubQueryConfig[];
}

export enum KpiV2FontSize {
  SMALL = 'small',
  LARGE = 'large',
}

export interface KpiV2SingleMetricOptions {
  kpiType: KpiV2Type.SINGLE_METRIC;
  section: KpiComponentSectionConfig;
  fontSize?: KpiV2FontSize;
}

export enum KpiV2Type {
  SINGLE_METRIC = 'singleMetric',
  DOUBLE_METRIC = 'doubleMetric',
}

export interface KpiV2DoubleMetricOptions {
  kpiType: KpiV2Type.DOUBLE_METRIC;
  sections: {
    leftSection: KpiComponentSectionConfig;
    rightSection: KpiComponentSectionConfig;
  };
  fontSize?: KpiV2FontSize;
}

export type KpiV2ComponentOptions =
  | KpiV2SingleMetricOptions
  | KpiV2DoubleMetricOptions;

export interface KpiV2ComponentConfig extends ChartGridConfig, BaseConfig {
  chartTypeConfig: KPIV2Config;
  options: KpiV2ComponentOptions;
}

export type ComponentConfig =
  | OvertimeTableComponentConfig
  | TableViewTimePeriodComponentConfig
  | KpiV2ComponentConfig
  | PieChartComponentConfig
  | BarChartComponentConfig
  | LineChartComponentConfig
  | CustomChartComponentConfig;

export interface StoryboardComponentDependencies {
  domainPreferences: DomainPreferences;
  storyboardDateUtils: StoryboardDateUtils;
}

export type PageConfig = {
  pageId: string;
  dashboardId: CustomDashboardId;
  pageNumber: number;
  enabled: boolean;
  title: LanguageString;
  link?: string;
  // TODO: Not sure about 'link' above. The behaviour I have now is that if link is provided,
  // it will use that link, otherwise, it will use the /custom/... link
  // The former will be used for pages which use the CustomDashboard component but the config
  // right now is stored in the legacy dashboard object, and the url is also the legacy dashboard url
  // without the 'custom' prefix
  components: (
    componentDependencies: StoryboardComponentDependencies
  ) => ComponentConfig[];
  gridCols?: GridCols;
  gridRows?: GridRows;
  header?: string; // markdown
  footer?: string; // markdown
} & (
  | { pageOptions?: DashboardPageOptions<true>; referenceDate?: never }
  | {
      pageOptions?: DashboardPageOptions<false>;
      // queries in most storyboard configs are defined in a relative way(now, last month, last year, etc)
      // reference date is the date used as base to derive those relative dates
      // it will default to now(currentMonth) if not provided
      // You can only provide it if timeslider is not enabled
      referenceDate?: string; // date in YYYY-MM-DD format
    }
);

type EnabledOption = {
  enabled?: boolean;
};

export type DashboardPageOptions<HasTimeSlider extends boolean = any> = {
  dataView?: EnabledOption & {};
  filterTray?: EnabledOption & {
    myFilters?: EnabledOption;
  };
  segmentationLevel1?: EnabledOption & {};
  segmentationLevel2?: EnabledOption & {};
  benchmark?: EnabledOption & {};
  forecast?: EnabledOption & {};
  displayOptions?: EnabledOption & {};
} & (HasTimeSlider extends true
  ? {
      timeSlider?: { enabled: true; type: TimeSliderTypes };
    }
  : {
      timeSlider?: { enabled: false };
    });

type CustomDashboardId = string;

export interface DashboardConfig {
  title: LanguageString;
  id: CustomDashboardId;
  enabled: boolean;
  pages: PageConfig[];
}

export interface PageHandle {
  selected: PageConfig;
  pages: PageConfig[];
  setter: (page: PageConfig) => void;
}

export type DashboardId = Dashboards;
export type DashboardPageId = DashboardPages;

export interface DashboardIdAndPageId {
  dashboardId: DashboardId;
  pageId: DashboardPageId;
}

export interface NonCoreDashboardIdAndPageId {
  dashboardId: DashboardId;
  pageId: NonCoreDashboardPageToChartSettingKeys;
}

type Description = string;
type DescriptionKey = string;
type ImagePath = string;
type Link = string;
type Name = string;
type NameKey = string;

export interface DashboardPage {
  description: Description;
  descriptionKey: DescriptionKey;
  enabled: boolean;
  imagePath: ImagePath;
  link: Link;
  name: Name;
  nameKey: NameKey;
  pageId: DashboardPageId;
  pageNumber: number;
  dashboardId: DashboardId;
  pageComponent?: any;
  pageOptions?: { disableDataView?: boolean };
}

export interface Dashboard {
  description: Description;
  descriptionKey: DescriptionKey;
  enabled: boolean;
  id: DashboardId;
  imagePath: ImagePath;
  link: Link;
  name: Name;
  nameKey: NameKey;
  pages: DashboardPage[];
}

export interface DashboardList {
  dashboards: Dashboard[];
}

export enum Dashboards {
  // Note: the actual values should be ids as in the backend config for these dashboards
  ATTRITION = 'attrition',
  EVALUATION = 'evaluation',
  GENDER_DIVERSITY = 'genderDiversity',
  JOINERS_AND_LEAVERS = 'joinersAndLeavers',
  MEDIAN_BASE = 'medianBase',
  MOVEMENTS = 'movement',
  PAYROLL = 'payroll',
  PEOPLE_BALANCE_SHEET = 'peopleBalanceSheet',
  RECRUITMENT_BALANCE_SHEET = 'recruitmentBalanceSheet',
  PEOPLE_SNAPSHOT = 'peopleSnapshot',
  RECRUITMENT_FUNNEL = 'recruitmentFunnel',
  RECRUITMENT_OVERVIEW = 'recruitment',
  SURVEY = 'survey',
  WORK_HOURS_AND_OT = 'workHoursAndOt',
}

// The ids should not be changed, ideally they should come from the backend
// so we can ensure that they're the same
export enum DashboardPages {
  ATTRITION_OVERVIEW = 'attrition-overview',
  GENDER_DIVERSITY_DEMOGRAPHICS = 'gender-diversity-demographics',
  GENDER_DIVERSITY_ATTRITION = 'gender-diversity-attrition',
  JOINERS_AND_LEAVERS_OVERVIEW = 'joiners-and-leavers-overview',
  COMPANY_STRUCTURE_ORGANIZATIONS = 'company-structure-organizations',
  COMPANY_STRUCTURE_LOCATIONS = 'company-structure-locations',
  COMPANY_STRUCTURE_JOB_GRADES = 'company-structure-job-grades',
  COMPANY_STRUCTURE_FUNCTIONS = 'company-structure-functions',
  COMPANY_STRUCTURE_EMPLOYMENT_TYPES = 'company-structure-employment-types',
  MEDIAN_BASE_OVERVIEW = 'median-base-overview',
  MEDIAN_BASE_KEY_METRICS = 'median-base-key-metrics',
  ONA_TRAINS_GRAPH = 'ona-trains-graph',
  RECRUITMENT_FUNNEL_OVERVIEW = 'recruitment-funnel-overview',
  PAYROLL_COST_TO_COMPANY = 'payroll-cost-to-company',
  PAYROLL_COST_TO_EMPLOYEE = 'payroll-cost-to-employee',
  SNAPSHOT_HEADCOUNT = 'snapshot-headcount',
  SNAPSHOT_KPIS = 'snapshot-kpis',
  SNAPSHOT_ORG_METRICS = 'snapshot-org-metrics',
  RBS_OVERVIEW = 'rbs-overview',
  RBS_METRICS_OVER_TIME = 'rbs-metrics-over-time',
  RBS_ISO_DEFINITIONS = 'rbs-iso-definitions',
  RELATIONSHIP_TO_CLIENTS_OVERVIEW = 'relationship-to-clients-overview',
  RELATIONSHIP_TO_CLIENTS_SINGLE_CLIENT = 'relationship-to-clients-single-client',
  RELATIONSHIP_TO_CLIENTS_OVER_TIME = 'relationship-to-clients-over-time',
  WORKHOURS_AND_OT_WORKHOURS = 'workhours-and-ot-workhours',
  WORKHOURS_AND_OT_OVERTIME = 'workhours-and-ot-overtime',
  PBS_OVERVIEW = 'pbs-overview',
  PBS_METRICS_OVER_TIME = 'pbs-metrics-over-time',
  MOVEMENT_OVERVIEW = 'movement-overview',
  MOVEMENT_NO_MOVEMENTS = 'movement-no-movements',
  RECRUITMENT_OVERVIEW = 'recruitment-overview',
  RECRUITMENT_ACTIVITIES = 'recruitment-activities',
  ONA_BS_OVERVIEW = 'ona-bs-overview',
  ONA_METRICS_OVER_TIME = 'ona-metrics-over-time',
  SURVEY_OVERALL_QUESTIONS = 'survey-overall-questions',
  SURVEY_SENTIMENT = 'survey-sentiment',
  ONA_GRAPH = 'ona-graph',
  ONA_GRAVITY = 'ona-gravity',
  EVALUATION_OVERVIEW = 'evaluation-overview',
  MULTICYCLE_EVALUATION = 'multicycle-evaluation',
  ATTRITION_RISK_OVERVIEW = 'attrition-risk-overview',
  ATTRITION_RISK_EMPLOYEES = 'attrition-risk-employees',
  ATTRITION_RISK_MANAGERS = 'attrition-risk-managers',
}
